// Booking System Types
// Since we're using JavaScript, these are documentation for expected data structures

/**
 * @typedef {Object} Category
 * @property {string} id
 * @property {string} name
 * @property {string} slug
 * @property {string} description
 * @property {string} icon
 * @property {number} displayOrder
 * @property {boolean} isActive
 */

/**
 * @typedef {Object} Package
 * @property {string} id
 * @property {string} categoryId
 * @property {string} categoryName
 * @property {string} name
 * @property {string} slug
 * @property {string} description
 * @property {number} price
 * @property {number} durationMinutes
 * @property {number|null} maxAttendance
 * @property {string[]} inclusions
 * @property {string[]} requirements
 * @property {string[]} images
 * @property {string[]} availableAddOns
 * @property {number} displayOrder
 * @property {boolean} isActive
 */

/**
 * @typedef {Object} AddOn
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {number} price
 * @property {'per_person'|'per_design'|'flat'} unitType
 * @property {boolean} isActive
 */

/**
 * @typedef {'PENDING_DEPOSIT'|'CONFIRMED'|'COMPLETED'|'CANCELLED'|'NO_SHOW'} BookingStatus
 */

/**
 * @typedef {Object} Booking
 * @property {string} id
 * @property {string} bookingNumber
 * @property {string|null} clientId
 * @property {string} clientName
 * @property {string} clientPhone
 * @property {string} clientEmail
 * @property {string|null} clientInstagram
 * @property {Date|string} eventDate
 * @property {string|null} eventTime
 * @property {string|null} eventLocation
 * @property {string} eventType
 * @property {Date|string|null} hennaDate
 * @property {string|null} hennaTime
 * @property {BookingStatus} status
 * @property {number} depositPercent
 * @property {number} depositAmount
 * @property {number} totalAmount
 * @property {number} paidAmount
 * @property {number} remainingAmount
 * @property {number} discountAmount
 * @property {string|null} specialRequests
 * @property {string|null} adminNotes
 * @property {'website'|'whatsapp'|'instagram'|'referral'|'other'} source
 * @property {boolean} isExpress
 * @property {number} itemCount
 * @property {number} paymentCount
 * @property {Date|string|null} confirmedAt
 * @property {Date|string|null} completedAt
 * @property {Date|string|null} cancelledAt
 * @property {string|null} cancellationReason
 * @property {string} createdBy
 * @property {Date|string} createdAt
 * @property {Date|string} updatedAt
 */

/**
 * @typedef {Object} BookingItem
 * @property {string} id
 * @property {'PACKAGE'|'ADD_ON'} itemType
 * @property {string|null} packageId
 * @property {Object|null} packageSnapshot
 * @property {Object|null} addOnSnapshot
 * @property {number} priceSnapshot
 * @property {number} quantity
 * @property {number} subtotal
 * @property {Object|null} addOnDetails
 * @property {number} displayOrder
 */

/**
 * @typedef {'DEPOSIT'|'INSTALLMENT'|'FULL_PAYMENT'|'REMAINING'|'REFUND'} PaymentType
 */

/**
 * @typedef {'PENDING'|'VERIFIED'|'REJECTED'} PaymentStatus
 */

/**
 * @typedef {'TRANSFER'|'CASH'|'EWALLET'|'CARD'} PaymentMethod
 */

/**
 * @typedef {Object} Payment
 * @property {string} id
 * @property {string} paymentNumber
 * @property {PaymentType} paymentType
 * @property {number} amount
 * @property {PaymentMethod} paymentMethod
 * @property {string|null} paymentChannel
 * @property {string|null} transactionReference
 * @property {Date|string} paymentDate
 * @property {string|null} proofImage
 * @property {PaymentStatus} status
 * @property {string|null} notes
 * @property {string|null} verifiedBy
 * @property {Date|string|null} verifiedAt
 * @property {string|null} rejectionReason
 * @property {Date|string} createdAt
 * @property {Date|string} updatedAt
 */

/**
 * @typedef {Object} Client
 * @property {string} id
 * @property {string} name
 * @property {string} email
 * @property {string} phone
 * @property {string|null} instagram
 * @property {string|null} address
 * @property {string|null} notes
 * @property {number} totalBookings
 * @property {number} totalSpent
 * @property {Date|string|null} lastBookingDate
 * @property {boolean} isRepeatClient
 * @property {Date|string} createdAt
 * @property {Date|string} updatedAt
 */

// Export constants
export const BOOKING_STATUS = {
  PENDING_DEPOSIT: "PENDING_DEPOSIT",
  CONFIRMED: "CONFIRMED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  NO_SHOW: "NO_SHOW",
};

export const PAYMENT_TYPE = {
  DEPOSIT: "DEPOSIT",
  INSTALLMENT: "INSTALLMENT",
  FULL_PAYMENT: "FULL_PAYMENT",
  REMAINING: "REMAINING",
  REFUND: "REFUND",
};

export const PAYMENT_STATUS = {
  PENDING: "PENDING",
  VERIFIED: "VERIFIED",
  REJECTED: "REJECTED",
};

export const PAYMENT_METHOD = {
  TRANSFER: "TRANSFER",
  CASH: "CASH",
  EWALLET: "EWALLET",
  CARD: "CARD",
};

export const BOOKING_SOURCE = {
  WEBSITE: "website",
  WHATSAPP: "whatsapp",
  INSTAGRAM: "instagram",
  REFERRAL: "referral",
  OTHER: "other",
};

// Status colors for UI
export const STATUS_COLORS = {
  [BOOKING_STATUS.PENDING_DEPOSIT]: "bg-yellow-100 text-yellow-800",
  [BOOKING_STATUS.CONFIRMED]: "bg-green-100 text-green-800",
  [BOOKING_STATUS.COMPLETED]: "bg-blue-100 text-blue-800",
  [BOOKING_STATUS.CANCELLED]: "bg-red-100 text-red-800",
  [BOOKING_STATUS.NO_SHOW]: "bg-gray-100 text-gray-800",
};

export const PAYMENT_STATUS_COLORS = {
  [PAYMENT_STATUS.PENDING]: "bg-yellow-100 text-yellow-800",
  [PAYMENT_STATUS.VERIFIED]: "bg-green-100 text-green-800",
  [PAYMENT_STATUS.REJECTED]: "bg-red-100 text-red-800",
};

// Helper functions
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatDateShort = (date) => {
  if (!date) return "-";

  // Handle Firebase Timestamp (has toDate() method or seconds property)
  if (typeof date === "object" && date !== null) {
    if (typeof date.toDate === "function") {
      return date.toDate().toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
    // Handle timestamp with seconds property
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
  }

  // Handle JavaScript Date object
  if (date instanceof Date) {
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  // Handle string dates
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";

  return d.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const getStatusLabel = (status) => {
  const labels = {
    [BOOKING_STATUS.PENDING_DEPOSIT]: "Menunggu Deposit",
    [BOOKING_STATUS.CONFIRMED]: "Dikonfirmasi",
    [BOOKING_STATUS.COMPLETED]: "Selesai",
    [BOOKING_STATUS.CANCELLED]: "Dibatalkan",
    [BOOKING_STATUS.NO_SHOW]: "Tidak Hadir",
  };
  return labels[status] || status;
};

export const getPaymentTypeLabel = (type) => {
  const labels = {
    [PAYMENT_TYPE.DEPOSIT]: "Deposit",
    [PAYMENT_TYPE.INSTALLMENT]: "Cicilan",
    [PAYMENT_TYPE.FULL_PAYMENT]: "Pelunasan",
    [PAYMENT_TYPE.REMAINING]: "Pelunasan",
    [PAYMENT_TYPE.REFUND]: "Pengembalian",
  };
  return labels[type] || type;
};

export const getPaymentStatusLabel = (status) => {
  const labels = {
    [PAYMENT_STATUS.PENDING]: "Menunggu Verifikasi",
    [PAYMENT_STATUS.VERIFIED]: "Terverifikasi",
    [PAYMENT_STATUS.REJECTED]: "Ditolak",
  };
  return labels[status] || status;
};
